import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { devLog } from '@/utils/logger';
import type { LeaveRequest, LeaveRequestWithProfile, CreateLeaveRequestInput, UpdateLeaveRequestInput, Profile, LeavePayrollVerificationStatus } from './types';
import type { ShiftConflict, EligibleReplacement, ConflictResolutionPayload, ConflictResolutionResult } from './conflictTypes';

// Error code to user-friendly message mapping
const ERROR_CODE_MESSAGES: Record<string, string> = {
  'INSUFFICIENT_BALANCE': 'Insufficient leave balance. Please check your available days.',
  'NO_POLICY': 'No leave policy found for this leave type. Please contact your administrator.',
  'NEGATIVE_BALANCE_NOT_ALLOWED': 'Your leave policy does not allow negative balance.',
  'EXCEEDS_MAX_CARRY_OVER': 'Request exceeds maximum carry-over limit.',
  'INVALID_DATE_RANGE': 'Invalid date range. End date must be after start date.',
  'OVERLAPPING_REQUEST': 'You already have a leave request for these dates.',
};

const handleLeaveError = (error: any, operation: string) => {
  console.error(`[Leave Service] ${operation} failed:`, error);
  
  // Map common errors
  if (error.code === 'PGRST116') {
    throw new Error('Leave request not found');
  }
  if (error.code === '42501') {
    throw new Error('Insufficient permissions');
  }
  
  // Concurrency conflict — another manager already processed this request
  const msg = (error.message || '').toLowerCase();
  if (msg.includes('status_conflict')) {
    throw new Error('This leave request has already been processed by another manager.');
  }
  
  throw new Error(error.message || `Failed to ${operation}`);
};

/**
 * Batch-fetch payroll verification status for leave requests.
 * Returns a map of leave request ID -> payroll status
 * 
 * Logic:
 * - Match leave requests to payslips by profile_id and date overlap
 * - Check the linked payroll_run status (completed vs rolled_back)
 * - Only approved leave can be payroll-verified
 */
async function batchFetchLeavePayrollStatus(
  leaveRequests: LeaveRequestWithProfile[]
): Promise<Map<string, LeavePayrollVerificationStatus>> {
  const result = new Map<string, LeavePayrollVerificationStatus>();
  
  if (leaveRequests.length === 0) return result;
  
  // Get unique profile IDs from approved leave requests
  const approvedRequests = leaveRequests.filter(r => r.status === 'approved');
  if (approvedRequests.length === 0) {
    // Set all non-approved to null
    leaveRequests.forEach(r => result.set(r.id, null));
    return result;
  }
  
  // Filter out null/undefined profile_ids (orphaned requests from deleted employees)
  // to prevent "invalid input syntax for type uuid: null" errors in PostgREST .in() filter
  const profileIds = [...new Set(
    approvedRequests.map(r => r.profile_id).filter((id): id is string => !!id)
  )];
  if (profileIds.length === 0) {
    leaveRequests.forEach(r => result.set(r.id, null));
    return result;
  }
  
  // Get date range boundaries for payslip lookup
  const allDates = approvedRequests.flatMap(r => [r.start_date, r.end_date]);
  const minDate = allDates.reduce((a, b) => a < b ? a : b);
  const maxDate = allDates.reduce((a, b) => a > b ? a : b);
  
  try {
    // Fetch payslips with their payroll run status that overlap with leave date ranges
    const { data: payslips, error } = await supabase
      .from('payslips')
      .select(`
        id,
        profile_id,
        start_date,
        end_date,
        created_at,
        payroll_run_id,
        payroll_runs!inner (
          id,
          status
        )
      `)
      .in('profile_id', profileIds)
      .lte('start_date', maxDate)
      .gte('end_date', minDate)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('[Leave Payroll Status] Error fetching payslips:', error);
      leaveRequests.forEach(r => result.set(r.id, null));
      return result;
    }
    
    if (!payslips || payslips.length === 0) {
      leaveRequests.forEach(r => result.set(r.id, null));
      return result;
    }
    
    // For each approved leave request, check if it overlaps with a payslip
    for (const request of leaveRequests) {
      // Only approved requests can show payroll status
      if (request.status !== 'approved') {
        result.set(request.id, null);
        continue;
      }
      
      // Find a payslip that covers this leave request's dates
      const matchingPayslip = payslips.find(p => 
        p.profile_id === request.profile_id &&
        p.start_date <= request.end_date &&
        p.end_date >= request.start_date
      );
      
      if (!matchingPayslip) {
        result.set(request.id, null);
      } else {
        const runStatus = (matchingPayslip.payroll_runs as any)?.status || 'unknown';
        if (runStatus === 'completed') {
          result.set(request.id, 'verified');
        } else if (runStatus === 'rolled_back') {
          result.set(request.id, 'rolled_back');
        } else {
          result.set(request.id, null);
        }
      }
    }
    
    return result;
  } catch (err) {
    console.error('[Leave Payroll Status] Exception:', err);
    leaveRequests.forEach(r => result.set(r.id, null));
    return result;
  }
}

export const leaveService = {
  // Employee operations
  async getMyLeaveRequests(profileId: string): Promise<LeaveRequest[]> {
    try {
      // Ensure user context is set before making queries that involve RLS policies
      await supabase.rpc('ensure_user_context');
      
      const { data, error } = await supabase
        .from('leave_requests')
        .select('*')
        .eq('profile_id', profileId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      handleLeaveError(error, 'get my leave requests');
      throw error;
    }
  },

  async createLeaveRequest(request: CreateLeaveRequestInput & { profile_id: string; company_id: string }): Promise<LeaveRequest> {
    try {
      devLog('[leaveService] Step 1: Creating leave request with policy validation');
      devLog('[leaveService] Step 2: Request details:', {
        leave_type: request.leave_type,
        start_date: request.start_date,
        end_date: request.end_date,
        reason: request.reason,
        profile_id: request.profile_id,
        company_id: request.company_id
      });
      
      // Use the new policy-validated creation function with part-day support
      devLog('[leaveService] Step 3: Calling create_leave_request_with_validation RPC');
      devLog('[leaveService] Step 3a: Part-day info:', { 
        is_part_day: request.is_part_day, 
        requested_hours: request.requested_hours,
        start_time: request.start_time,
        end_time: request.end_time
      });
      const { data, error } = await supabase.rpc('create_leave_request_with_validation', {
        p_leave_type: request.leave_type,
        p_start_date: request.start_date,
        p_end_date: request.end_date,
        p_reason: request.reason || null,
        p_requested_days: null, // Let function calculate
        p_policy_id: null, // Let function find appropriate policy
        p_requested_hours: request.is_part_day && request.requested_hours ? request.requested_hours : null,
        p_start_time: request.is_part_day && request.start_time ? request.start_time : null,
        p_end_time: request.is_part_day && request.end_time ? request.end_time : null
      });

      devLog('[leaveService] Step 4: RPC response received');
      devLog('[leaveService] Step 4a: Raw data:', JSON.stringify(data, null, 2));
      devLog('[leaveService] Step 4b: Error object:', error);

      if (error) {
        console.error('[leaveService] Step 5 ERROR: RPC returned error:', error);
        throw error;
      }

      // Parse the response data - could be object or array
      let responseData: any = data;
      
      // Handle array response
      if (Array.isArray(data) && data.length > 0) {
        responseData = data[0];
        devLog('[leaveService] Step 5: Parsed array response, first row:', responseData);
      } else {
        devLog('[leaveService] Step 5: Response is object:', responseData);
      }

      // Check for validation failure in response
      if (responseData && responseData.success === false) {
        console.error('[leaveService] Step 6 ERROR: Validation failed in RPC response:', {
          success: responseData.success,
          error_code: responseData.error_code,
          error_message: responseData.error_message
        });
        
        // Prioritize detailed backend message over generic mappings
        const userMessage = responseData.error_message || 
                           ERROR_CODE_MESSAGES[responseData.error_code] || 
                           'Failed to create leave request';
        
        throw new Error(userMessage);
      }

      // Check if we have a valid leave_request_id
      if (!responseData || !responseData.leave_request_id) {
        console.error('[leaveService] Step 6 ERROR: No leave_request_id in response:', responseData);
        throw new Error('Failed to create leave request - no request ID returned');
      }

      devLog('[leaveService] Step 6: Leave request created successfully, ID:', responseData.leave_request_id);

      // Fetch the created leave request
      devLog('[leaveService] Step 7: Fetching created leave request');
      const { data: leaveRequest, error: fetchError } = await supabase
        .from('leave_requests')
        .select('*')
        .eq('id', responseData.leave_request_id)
        .single();

      if (fetchError) {
        console.error('[leaveService] Step 8 ERROR: Failed to fetch created request:', fetchError);
        throw fetchError;
      }

      devLog('[leaveService] Step 8: Successfully fetched created leave request:', leaveRequest);
      return leaveRequest;
    } catch (error: any) {
      console.error('[leaveService] Step FINAL ERROR: createLeaveRequest failed:', error);
      
      // Re-throw with the error message if it's already a nice message
      if (error.message && !error.message.includes('undefined')) {
        throw error;
      }
      
      handleLeaveError(error, 'create leave request');
      throw error;
    }
  },

  // Manager operations
  async getCompanyLeaveRequests(options?: {
    employeeId?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ data: LeaveRequestWithProfile[]; totalCount: number; hasMore: boolean }> {
    try {
      // Ensure user context is set before making queries that involve RLS policies
      await supabase.rpc('ensure_user_context');
      
      const limit = options?.limit ?? 50;
      const offset = options?.offset ?? 0;
      
      let query = supabase
        .from('leave_requests')
        .select(`
          *,
          profile:profiles!inner(
            first_name,
            last_name,
            email,
            avatar_url
          )
        `, { count: 'exact' })
        .order('created_at', { ascending: false });

      // Filter by specific employee if provided
      if (options?.employeeId) {
        query = query.eq('profile_id', options.employeeId);
      }

      // Filter by status if provided (e.g., 'pending')
      if (options?.status) {
        query = query.eq('status', options.status);
      }

      // Apply pagination
      query = query.range(offset, offset + limit - 1);

      const { data, error, count } = await query;

      if (error) throw error;
      
      const totalCount = count ?? 0;
      return {
        data: data || [],
        totalCount,
        hasMore: offset + limit < totalCount,
      };
    } catch (error) {
      handleLeaveError(error, 'get company leave requests');
      throw error;
    }
  },

  // Filtered company leave requests (server-side filtering per Leave_approval_filter.md)
  // Includes payroll verification status for approved requests
  async getFilteredCompanyLeaveRequests(filters: {
    month: string | null;
    employeeIds: string[];
    leaveTypes: string[];
    statuses: string[];
    limit?: number;
    offset?: number;
  }): Promise<{ data: LeaveRequestWithProfile[]; totalCount: number; hasMore: boolean }> {
    try {
      const limit = filters.limit ?? 50;
      const offset = filters.offset ?? 0;
      
      const { data, error } = await supabase.rpc('get_filtered_company_leave_requests', {
        p_month: filters.month || null,
        p_employee_ids: filters.employeeIds.length > 0 ? filters.employeeIds : null,
        p_leave_types: filters.leaveTypes.length > 0 ? filters.leaveTypes : null,
        p_statuses: filters.statuses.length > 0 ? filters.statuses : null,
        p_limit: limit,
        p_offset: offset,
      });

      if (error) throw error;

      // Transform the flat response to match LeaveRequestWithProfile shape
      const leaveRequests: LeaveRequestWithProfile[] = (data || []).map((row: any) => ({
        id: row.id,
        profile_id: row.profile_id,
        company_id: row.company_id,
        leave_type: row.leave_type,
        start_date: row.start_date,
        end_date: row.end_date,
        reason: row.reason,
        status: row.status,
        created_at: row.created_at,
        updated_at: row.updated_at,
        policy_id: row.policy_id,
        requested_days: row.requested_days,
        requested_hours: row.requested_hours,
        balance_at_request: row.balance_at_request,
        auto_approved: row.auto_approved,
        start_time: row.start_time,
        end_time: row.end_time,
        profile: {
          first_name: row.profile_first_name,
          last_name: row.profile_last_name,
          email: row.profile_email,
          avatar_url: row.profile_avatar_url,
        },
      }));

      // Extract total_count from first row (window function)
      const totalCount = data?.[0]?.total_count ?? 0;
      
      // Batch fetch payroll verification status for all requests
      const payrollStatusMap = await batchFetchLeavePayrollStatus(leaveRequests);

      // Merge payroll status into each leave request
      const result = leaveRequests.map(request => ({
        ...request,
        payroll_status: payrollStatusMap.get(request.id) || null,
      }));
      
      return {
        data: result,
        totalCount,
        hasMore: offset + limit < totalCount,
      };
    } catch (error) {
      handleLeaveError(error, 'get filtered company leave requests');
      throw error;
    }
  },

  async updateLeaveRequestStatus(requestId: string, status: 'approved' | 'rejected', reason?: string): Promise<{ profileId: string; companyId: string; leaveType: string; startDate: string; endDate: string } | void> {
    try {
      devLog('[leaveService] updateLeaveRequestStatus - Starting:', { requestId, status });
      
      // First get the leave request details for notification
      const { data: requestData, error: queryError } = await supabase
        .from('leave_requests')
        .select('profile_id, company_id, leave_type, start_date, end_date')
        .eq('id', requestId)
        .single();

      if (queryError) {
        console.warn('[leaveService] updateLeaveRequestStatus - Query error (notification will be skipped):', queryError);
      }
      
      if (!requestData) {
        console.warn('[leaveService] updateLeaveRequestStatus - No request data found for notification');
      } else {
        devLog('[leaveService] updateLeaveRequestStatus - Request data retrieved:', {
          profileId: requestData.profile_id,
          companyId: requestData.company_id,
          leaveType: requestData.leave_type,
        });
      }

      // Map frontend status to backend parameter format
      // Note: 3-param function uses 'approved'/'rejected' (status values) not 'approve'/'reject' (verbs)
      const newStatus = status === 'approved' ? 'approved' : 'rejected';
      
      const { error } = await supabase.rpc('process_leave_request_balance', {
        p_request_id: requestId,
        p_new_status: newStatus,
        p_deduct_balance: true,
        p_reason: reason || null,
      });

      if (error) throw error;

      devLog('[leaveService] updateLeaveRequestStatus - RPC success');

      // Return request info for notification
      if (requestData) {
        const notificationData = {
          profileId: requestData.profile_id,
          companyId: requestData.company_id,
          leaveType: requestData.leave_type,
          startDate: requestData.start_date,
          endDate: requestData.end_date,
        };
        devLog('[leaveService] updateLeaveRequestStatus - Returning notification data:', notificationData);
        return notificationData;
      }
      
      console.warn('[leaveService] updateLeaveRequestStatus - Returning void (no notification data)');
    } catch (error) {
      handleLeaveError(error, 'update leave request status');
      throw error;
    }
  },

  async adminUpdateLeaveRequestStatus(requestId: string, status: string, reason?: string): Promise<{ profileId: string; companyId: string; leaveType: string; startDate: string; endDate: string } | void> {
    try {
      devLog('[leaveService] adminUpdateLeaveRequestStatus - Starting:', { requestId, status, reason: reason ? '(provided)' : '(none)' });
      
      // First get the leave request details for notification (same pattern as updateLeaveRequestStatus)
      const { data: requestData, error: queryError } = await supabase
        .from('leave_requests')
        .select('profile_id, company_id, leave_type, start_date, end_date')
        .eq('id', requestId)
        .single();

      if (queryError) {
        console.warn('[leaveService] adminUpdateLeaveRequestStatus - Query error (notification will be skipped):', queryError);
      }
      
      if (!requestData) {
        console.warn('[leaveService] adminUpdateLeaveRequestStatus - No request data found for notification');
      } else {
        devLog('[leaveService] adminUpdateLeaveRequestStatus - Request data retrieved:', {
          profileId: requestData.profile_id,
          companyId: requestData.company_id,
          leaveType: requestData.leave_type,
        });
      }
      
      const { data, error } = await supabase.rpc('admin_update_leave_request_status', {
        leave_request_id: requestId,
        new_status: status,
        p_reason: reason || null
      });

      devLog('[leaveService] adminUpdateLeaveRequestStatus - RPC response:', { data, error });

      if (error) {
        console.error('[leaveService] adminUpdateLeaveRequestStatus - ERROR:', error);
        throw error;
      }
      
      devLog('[leaveService] adminUpdateLeaveRequestStatus - RPC success');
      
      // Return request info for notification
      if (requestData) {
        const notificationData = {
          profileId: requestData.profile_id,
          companyId: requestData.company_id,
          leaveType: requestData.leave_type,
          startDate: requestData.start_date,
          endDate: requestData.end_date,
        };
        devLog('[leaveService] adminUpdateLeaveRequestStatus - Returning notification data:', notificationData);
        return notificationData;
      }
      
      console.warn('[leaveService] adminUpdateLeaveRequestStatus - Returning void (no notification data)');
    } catch (error) {
      handleLeaveError(error, 'admin update leave request status');
      throw error;
    }
  },

  async updateMyLeaveRequest(requestId: string, request: UpdateLeaveRequestInput): Promise<void> {
    try {
      devLog('[leaveService] updateMyLeaveRequest - Starting:', { requestId, request });
      devLog('[leaveService] updateMyLeaveRequest - Part-day info:', {
        is_part_day: request.is_part_day,
        requested_hours: request.requested_hours,
        start_time: request.start_time,
        end_time: request.end_time
      });

      const { error } = await supabase.rpc('update_user_leave_request', {
        p_request_id: requestId,
        p_leave_type: request.leave_type,
        p_start_date: request.start_date,
        p_end_date: request.end_date,
        p_reason: request.reason || null,
        p_requested_hours: request.is_part_day && request.requested_hours 
          ? request.requested_hours 
          : null,
        p_start_time: request.is_part_day && request.start_time ? request.start_time : null,
        p_end_time: request.is_part_day && request.end_time ? request.end_time : null
      });

      if (error) throw error;
      devLog('[leaveService] updateMyLeaveRequest - SUCCESS');
    } catch (error) {
      handleLeaveError(error, 'update leave request');
      throw error;
    }
  },

  async cancelMyLeaveRequest(requestId: string): Promise<void> {
    try {
      devLog('[leaveService] cancelMyLeaveRequest - Step 1: Starting for request_id:', requestId);
      const { error } = await supabase.rpc('cancel_user_leave_request', {
        p_request_id: requestId
      });

      if (error) {
        console.error('[leaveService] cancelMyLeaveRequest - ERROR:', error);
        throw error;
      }
      devLog('[leaveService] cancelMyLeaveRequest - SUCCESS');
    } catch (error) {
      handleLeaveError(error, 'cancel leave request');
      throw error;
    }
  },

  // Edit approved leave request - changes status back to pending and restores balance
  async editApprovedLeaveRequest(requestId: string, request: UpdateLeaveRequestInput): Promise<{
    requestId: string;
    profileId: string;
    companyId: string;
    leaveType: string;
    startDate: string;
    endDate: string;
  } | void> {
    try {
      devLog('[leaveService] editApprovedLeaveRequest - Step 1: Starting');
      devLog('[leaveService] editApprovedLeaveRequest - Input:', { requestId, request });
      devLog('[leaveService] editApprovedLeaveRequest - Part-day info:', {
        is_part_day: request.is_part_day,
        requested_hours: request.requested_hours,
        start_time: request.start_time,
        end_time: request.end_time
      });

      // Fetch request metadata BEFORE RPC for notification (proven pattern from updateLeaveRequestStatus)
      const { data: requestData, error: queryError } = await supabase
        .from('leave_requests')
        .select('profile_id, company_id, leave_type, start_date, end_date')
        .eq('id', requestId)
        .single();

      if (queryError) {
        console.warn('[leaveService] editApprovedLeaveRequest - Query error (notification will be skipped):', queryError);
      }

      if (!requestData) {
        console.warn('[leaveService] editApprovedLeaveRequest - No request data found for notification');
      } else {
        devLog('[leaveService] editApprovedLeaveRequest - Request data retrieved:', {
          profileId: requestData.profile_id,
          companyId: requestData.company_id,
        });
      }

      const { data, error } = await supabase.rpc('edit_approved_leave_request', {
        p_request_id: requestId,
        p_leave_type: request.leave_type,
        p_start_date: request.start_date,
        p_end_date: request.end_date,
        p_reason: request.reason || null,
        p_requested_hours: request.is_part_day && request.requested_hours 
          ? request.requested_hours 
          : null,
        p_start_time: request.is_part_day && request.start_time ? request.start_time : null,
        p_end_time: request.is_part_day && request.end_time ? request.end_time : null
      });

      devLog('[leaveService] editApprovedLeaveRequest - Step 2: RPC response:', { data, error });

      if (error) {
        console.error('[leaveService] editApprovedLeaveRequest - ERROR:', error);
        throw error;
      }

      // Check for business logic failure in response
      const response = (Array.isArray(data) ? data[0] : data) as { success?: boolean; error_code?: string; error_message?: string } | null;
      if (response && response.success === false) {
        console.error('[leaveService] editApprovedLeaveRequest - Business error:', response);
        // Prioritize detailed backend message over generic mappings
        const userMessage = response.error_message || ERROR_CODE_MESSAGES[response.error_code || ''] || 'Failed to edit approved leave request';
        throw new Error(userMessage);
      }

      devLog('[leaveService] editApprovedLeaveRequest - SUCCESS:', response);

      // Return metadata for notification (using NEW values from the updated request)
      if (requestData) {
        const notificationData = {
          requestId,
          profileId: requestData.profile_id,
          companyId: requestData.company_id,
          leaveType: request.leave_type,
          startDate: request.start_date,
          endDate: request.end_date,
        };
        devLog('[leaveService] editApprovedLeaveRequest - Returning notification data:', notificationData);
        return notificationData;
      }

      console.warn('[leaveService] editApprovedLeaveRequest - Returning void (no notification data)');
    } catch (error) {
      handleLeaveError(error, 'edit approved leave request');
      throw error;
    }
  },

  // Cancel approved leave request - restores balance
  async cancelApprovedLeaveRequest(requestId: string): Promise<{ daysRestored: number }> {
    try {
      devLog('[leaveService] cancelApprovedLeaveRequest - Step 1: Starting for request_id:', requestId);

      const { data, error } = await supabase.rpc('cancel_approved_leave_request', {
        p_request_id: requestId
      });

      devLog('[leaveService] cancelApprovedLeaveRequest - Step 2: RPC response:', { data, error });

      if (error) {
        console.error('[leaveService] cancelApprovedLeaveRequest - ERROR:', error);
        throw error;
      }

      // Check for business logic failure in response
      const response = (Array.isArray(data) ? data[0] : data) as { success?: boolean; error_code?: string; error_message?: string; days_restored?: number } | null;
      if (response && response.success === false) {
        console.error('[leaveService] cancelApprovedLeaveRequest - Business error:', response);
        // Prioritize detailed backend message over generic mappings
        const userMessage = response.error_message || ERROR_CODE_MESSAGES[response.error_code || ''] || 'Failed to cancel approved leave request';
        throw new Error(userMessage);
      }

      devLog('[leaveService] cancelApprovedLeaveRequest - SUCCESS:', response);
      return { daysRestored: response?.days_restored || 0 };
    } catch (error) {
      handleLeaveError(error, 'cancel approved leave request');
      throw error;
    }
  },

  // Shared operations
  async getCompanyEmployees(): Promise<Profile[]> {
    try {
      const { data, error } = await supabase.rpc('list_company_profiles');

      if (error) throw error;
      return data || [];
    } catch (error) {
      handleLeaveError(error, 'get company employees');
      throw error;
    }
  },

  // Conflict Resolution Methods (Phase 3)
  async checkLeaveConflicts(
    profileId: string,
    startDate: string,
    endDate: string,
    startTime?: string | null,
    endTime?: string | null
  ): Promise<ShiftConflict[]> {
    try {
      const { data, error } = await supabase.rpc('check_leave_shift_conflicts', {
        p_profile_id: profileId,
        p_start_date: startDate,
        p_end_date: endDate,
        p_start_time: startTime || null,
        p_end_time: endTime || null
      });
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      handleLeaveError(error, 'check leave conflicts');
      throw error;
    }
  },

  async getEligibleReplacements(
    shiftIds: string[],
    companyId: string
  ): Promise<EligibleReplacement[]> {
    try {
      const { data, error } = await supabase.rpc('get_eligible_shift_replacements', {
        p_shift_ids: shiftIds,
        p_company_id: companyId
      });
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      handleLeaveError(error, 'get eligible replacements');
      throw error;
    }
  },

  async approveLeaveWithResolution(
    payload: ConflictResolutionPayload
  ): Promise<ConflictResolutionResult> {
    try {
      const { data, error } = await supabase.rpc('approve_leave_with_conflict_resolution', {
        p_leave_request_id: payload.leaveRequestId,
        p_resolutions: JSON.parse(JSON.stringify(payload.resolutions)) // Cast to plain JSON
      });
      
      if (error) throw error;
      
      const result = Array.isArray(data) ? data[0] : data;
      if (!result?.success) {
        throw new Error(result?.error_message || 'Failed to approve leave with resolution');
      }
      return result as ConflictResolutionResult;
    } catch (error) {
      handleLeaveError(error, 'approve leave with resolution');
      throw error;
    }
  }
};
